import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle2,
  Database,
  Loader2,
  Search,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTITUICAO_POR_SEGMENTO = {
  INSS: "037 - INSS (PREVIDÊNCIA SOCIAL)",
  SIAPE: "131 - SERVIDOR FEDERAL CIVIL",
};

function formatarCpf(valor) {
  const numeros = String(valor || "").replace(/\D/g, "").slice(0, 11);
  return numeros
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

function formatarMoeda(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "Não informado";
  return numero.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function PreviewConsulta({ preview }) {
  const itens = [
    ["Cliente", preview.nome || "Nome não informado"],
    ["CPF", preview.cpf],
    ["Segmento", preview.segmento],
    ["Benefício", preview.beneficio || "Não informado"],
    ["Espécie", preview.especie || "Não informada"],
    ["Situação", preview.situacao || "Não informada"],
    ["Renda", formatarMoeda(preview.renda)],
    ["Margem disponível", formatarMoeda(preview.margem_consignavel)],
    ["Telefones encontrados", preview.telefones?.length || 0],
    ["Contratos encontrados", preview.quantidade_contratos || 0],
  ];

  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="consulta-preview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-emerald-800">
          <CheckCircle2 className="h-5 w-5" />
          Dados encontrados
        </CardTitle>
        <CardDescription>Confira o resumo antes de cadastrar ou atualizar o cliente.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {itens.map(([rotulo, valor]) => (
          <div key={rotulo} className="rounded-lg border border-emerald-100 bg-white p-3">
            <p className="text-xs font-medium text-slate-500">{rotulo}</p>
            <p className="mt-1 font-semibold text-slate-800">{valor}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function ConsultaCadastro() {
  const navigate = useNavigate();
  const [configuracao, setConfiguracao] = useState({ runner_configurado: false, segmentos: [] });
  const [cpf, setCpf] = useState("");
  const [segmento, setSegmento] = useState("AUTO");
  const [preparando, setPreparando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [cadastrando, setCadastrando] = useState(false);
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [consulta, setConsulta] = useState(null);
  const [resultado, setResultado] = useState(null);

  const empresa = useMemo(() => JSON.parse(localStorage.getItem("empresa") || "null"), []);
  const usuario = useMemo(() => JSON.parse(localStorage.getItem("usuario") || "null"), []);

  useEffect(() => {
    axios.get(`${API}/consultas/configuracao`)
      .then((response) => setConfiguracao(response.data))
      .catch(() => toast.error("Não foi possível carregar o módulo de consulta."));
  }, []);

  const prepararConsulta = async () => {
    setPreparando(true);
    setConsulta(null);
    setResultado(null);
    try {
      const response = await axios.post(`${API}/consultas/preparar`, { cpf, segmento });
      if (response.data.precisa_escolher_segmento) {
        toast.error("Não identificamos o segmento. Escolha uma opção para continuar.");
        return;
      }
      setPreflight(response.data);
      if (!response.data.runner_configurado) {
        toast.error("A fonte de consulta ainda precisa ser conectada ao CRM.");
        return;
      }
      setConfirmacaoAberta(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Confira o CPF e tente novamente.");
    } finally {
      setPreparando(false);
    }
  };

  const executarConsulta = async () => {
    if (!preflight?.segmento) return;
    setConfirmacaoAberta(false);
    setConsultando(true);
    try {
      const response = await axios.post(`${API}/consultas/executar`, {
        cpf,
        segmento: preflight.segmento,
        empresa_id: empresa?.id || null,
        atendente_id: usuario?.id || null,
        confirmar_custo: true,
      });
      setConsulta(response.data);
      toast.success("Consulta concluída. Confira os dados encontrados.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível concluir a consulta.");
    } finally {
      setConsultando(false);
    }
  };

  const cadastrarDados = async () => {
    if (!consulta?.consulta_id) return;
    setCadastrando(true);
    try {
      const response = await axios.post(`${API}/consultas/${consulta.consulta_id}/confirmar`, {
        empresa_id: empresa?.id || null,
        atendente_id: usuario?.id || null,
      });
      setResultado(response.data);
      toast.success("Cliente e contratos atualizados no CRM.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível cadastrar os dados.");
    } finally {
      setCadastrando(false);
    }
  };

  const irParaSimulacao = () => {
    const preview = consulta?.preview || {};
    const params = new URLSearchParams();
    if (resultado?.cliente_id) params.set("cliente_id", resultado.cliente_id);
    if (preflight?.segmento) params.set("segmento", preflight.segmento);
    if (preview.margem_consignavel != null) params.set("margem", preview.margem_consignavel);
    const instituicao = INSTITUICAO_POR_SEGMENTO[preflight?.segmento];
    if (instituicao) params.set("instituicao", instituicao);
    navigate(`/simulador?${params.toString()}`);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="consulta-cadastro-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Search className="h-7 w-7 text-emerald-600" />
          Consultar e cadastrar
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Consulte o CPF, atualize a ficha sem duplicidade e siga para a simulação.
        </p>
      </div>

      {!configuracao.runner_configurado && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex gap-3 pt-6 text-sm text-amber-900">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Módulo instalado; conexão externa pendente</p>
              <p className="mt-1 text-amber-800">
                Configure o runner seguro no servidor. Senhas do portal não ficam no CRM nem no código.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-emerald-600" />
            Dados da consulta
          </CardTitle>
          <CardDescription>Se não souber o segmento, deixe o sistema verificar.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="consulta-cpf">CPF</Label>
              <Input
                id="consulta-cpf"
                value={cpf}
                onChange={(event) => setCpf(formatarCpf(event.target.value))}
                placeholder="000.000.000-00"
                inputMode="numeric"
                data-testid="consulta-cpf"
              />
            </div>
            <div className="space-y-2">
              <Label>Segmento</Label>
              <Select value={segmento} onValueChange={setSegmento}>
                <SelectTrigger data-testid="consulta-segmento">
                  <SelectValue placeholder="Escolha o segmento" />
                </SelectTrigger>
                <SelectContent>
                  {(configuracao.segmentos || []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={prepararConsulta}
              disabled={preparando || consultando || cpf.replace(/\D/g, "").length !== 11}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="consulta-buscar"
            >
              {preparando || consultando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              {consultando ? "Consultando..." : "Consultar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {consulta?.preview && <PreviewConsulta preview={consulta.preview} />}

      {consulta?.preview && !resultado && (
        <div className="flex justify-end">
          <Button
            onClick={cadastrarDados}
            disabled={cadastrando}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="consulta-cadastrar"
          >
            {cadastrando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />}
            Cadastrar e continuar
          </Button>
        </div>
      )}

      {resultado && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="consulta-resultado">
          <CardContent className="flex flex-col gap-4 pt-6 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <p className="font-bold text-emerald-900">Cadastro pronto para simulação</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant="outline">{resultado.contratos_novos || 0} contratos novos</Badge>
                <Badge variant="outline">{resultado.contratos_existentes || 0} já existentes</Badge>
                {resultado.telefones_ignorados > 0 && (
                  <Badge variant="outline">{resultado.telefones_ignorados} telefone(s) mantido(s) como excluído(s)</Badge>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => navigate(`/clientes/${resultado.cliente_id}/editar`)}>
                Ver ficha
              </Button>
              <Button onClick={irParaSimulacao} className="bg-emerald-600 hover:bg-emerald-700">
                Simular oportunidades <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmacaoAberta} onOpenChange={setConfirmacaoAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar consulta</DialogTitle>
            <DialogDescription>
              Consultar o CPF {preflight?.cpf_mascarado} no segmento {preflight?.segmento}. Essa ação pode consumir crédito na fonte externa.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmacaoAberta(false)}>Cancelar</Button>
            <Button onClick={executarConsulta} className="bg-emerald-600 hover:bg-emerald-700">
              Confirmar e consultar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

